import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

type DottedSurfaceProps = Omit<React.ComponentProps<'div'>, 'ref'>;

// Simple inline cn helper to avoid external dependencies and keep the build lightweight
const cn = (...classes: (string | undefined | null | false)[]) => classes.filter(Boolean).join(' ');

export function DottedSurface({ className, ...props }: DottedSurfaceProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<{
        scene: THREE.Scene;
        camera: THREE.PerspectiveCamera;
        renderer: THREE.WebGLRenderer;
        geometry: THREE.BufferGeometry;
        material: THREE.PointsMaterial;
        animationId: number;
        count: number;
    } | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        let cleanupFn = () => {};

        try {
            const SEPARATION = 100;
            const AMOUNTX = 50;
            const AMOUNTY = 50;

            // Scene setup
            const scene = new THREE.Scene();
            // Transparent fog to blend nicely with the dark background
            scene.fog = new THREE.FogExp2(0x0f172a, 0.0005);

            const camera = new THREE.PerspectiveCamera(
                55,
                window.innerWidth / window.innerHeight,
                1,
                10000
            );
            
            // Position camera to look down at an elegant angle over the 3D surface
            camera.position.set(0, 500, 1000);
            camera.lookAt(new THREE.Vector3(0, 0, 0));

            const renderer = new THREE.WebGLRenderer({
                alpha: true,
                antialias: true,
                powerPreference: "high-performance"
            });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.setSize(window.innerWidth, window.innerHeight);

            containerRef.current.appendChild(renderer.domElement);

            // Create particles
            const positions: number[] = [];
            const colors: number[] = [];

            for (let ix = 0; ix < AMOUNTX; ix++) {
                for (let iy = 0; iy < AMOUNTY; iy++) {
                    const x = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2;
                    const y = 0; // Animating this
                    const z = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2;

                    positions.push(x, y, z);

                    // Driver Score Premium Amber/Orange Gradient theme particles!
                    // We mix deep orange, amber, and glowing yellow/white details
                    const ratio = ix / AMOUNTX;
                    const r = 0.9 + ratio * 0.1; // 0.9 - 1.0 (highly vibrant red/orange channel)
                    const g = 0.35 + (iy / AMOUNTY) * 0.25; // 0.35 - 0.6 (smooth amber/orange gradient)
                    const b = 0.05 + Math.sin(ix * 0.1) * 0.05; // very low blue content to maintain high-warmth amber glow

                    colors.push(r, g, b);
                }
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute(
                'position',
                new THREE.Float32BufferAttribute(positions, 3)
            );
            geometry.setAttribute(
                'color',
                new THREE.Float32BufferAttribute(colors, 3)
            );

            // Create material with glowing particles using circular styling
            const material = new THREE.PointsMaterial({
                size: 6,
                vertexColors: true,
                transparent: true,
                opacity: 0.25, // Subtle, modern background ambiance
                sizeAttenuation: true,
            });

            // Create points object
            const points = new THREE.Points(geometry, material);
            scene.add(points);

            let count = 0;
            let animationId: number;

            // Animation function
            const animate = () => {
                animationId = requestAnimationFrame(animate);

                const positionAttribute = geometry.attributes.position;
                const positionsArray = positionAttribute.array as Float32Array;

                let i = 0;
                for (let ix = 0; ix < AMOUNTX; ix++) {
                    for (let iy = 0; iy < AMOUNTY; iy++) {
                        const index = i * 3;

                        // Wave formula (Double Sine Wave for dynamic ripple effect)
                        positionsArray[index + 1] =
                            Math.sin((ix + count) * 0.25) * 45 +
                            Math.sin((iy + count) * 0.35) * 45;

                        i++;
                    }
                }

                positionAttribute.needsUpdate = true;
                renderer.render(scene, camera);
                count += 0.03; // Smooth speed
            };

            // Handle window resize
            const handleResize = () => {
                if (!containerRef.current) return;
                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(window.innerWidth, window.innerHeight);
            };

            window.addEventListener('resize', handleResize);

            // Start animation
            animate();

            // Store references
            sceneRef.current = {
                scene,
                camera,
                renderer,
                geometry,
                material,
                animationId,
                count,
            };

            cleanupFn = () => {
                window.removeEventListener('resize', handleResize);

                if (sceneRef.current) {
                    cancelAnimationFrame(sceneRef.current.animationId);

                    // Clean up Three.js objects to prevent GPU memory leaks
                    sceneRef.current.geometry.dispose();
                    sceneRef.current.material.dispose();
                    sceneRef.current.renderer.dispose();

                    if (containerRef.current && sceneRef.current.renderer.domElement) {
                        try {
                            containerRef.current.removeChild(sceneRef.current.renderer.domElement);
                        } catch (e) {
                            // Element might have been already removed during hot-reload
                        }
                    }
                }
            };
        } catch (error) {
            console.warn("WebGL is not supported or context could not be created on this device/browser. Falling back to static dark background.", error);
        }

        return () => cleanupFn();
    }, []);

    return (
        <div
            ref={containerRef}
            className={cn('pointer-events-none fixed inset-0 -z-10 w-full h-full bg-slate-950/20 overflow-hidden', className)}
            {...props}
        />
    );
}
